import React from 'react';
import {
  Text, Table, Thead, Tbody, Tr, Th, Td, UnorderedList, ListItem, OrderedList,
  Alert, AlertIcon, Code, HStack, Badge, VStack, Card, CardBody, Box, Heading,
} from '@chakra-ui/react';

// Moved from HelpPage.js — original Categories & Subcategories manual
const configManual = [
  {
    title: '1. Introdução — Categorias e Subcategorias',
    content: (
      <>
        <Text mb={3}>
          As <strong>Categorias</strong> e <strong>Subcategorias</strong> de profissões são a base para organizar os serviços oferecidos pelos trabalhadores no aplicativo TOVI. Cada trabalhador seleciona uma subcategoria de profissão ao se cadastrar, e essa subcategoria pertence a uma categoria principal.
        </Text>
        <Text fontWeight="medium" mb={1}>Exemplo:</Text>
        <UnorderedList ml={6} mb={3}>
          <ListItem><strong>Categoria:</strong> Construção Civil
            <UnorderedList ml={6}>
              <ListItem><strong>Subcategoria:</strong> Pedreiro</ListItem>
              <ListItem><strong>Subcategoria:</strong> Eletricista</ListItem>
              <ListItem><strong>Subcategoria:</strong> Encanador</ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          É necessário criar pelo menos uma <strong>Categoria</strong> antes de criar qualquer <strong>Subcategoria</strong>.
        </Alert>
      </>
    ),
    keywords: 'introdução categorias subcategorias profissões organizar serviços',
  },
  {
    title: '2. Acessando o Menu',
    content: (
      <>
        <OrderedList spacing={2}>
          <ListItem>Faça login no painel administrativo</ListItem>
          <ListItem>No menu lateral esquerdo, clique em <strong>"Config. Mobile App"</strong></ListItem>
          <ListItem>Dois itens aparecerão no submenu:
            <UnorderedList ml={6} mt={1}>
              <ListItem><strong>Categorias</strong> — para gerenciar as categorias principais</ListItem>
              <ListItem><strong>Sub-Categorias</strong> — para gerenciar as subcategorias (profissões)</ListItem>
            </UnorderedList>
          </ListItem>
        </OrderedList>
      </>
    ),
    keywords: 'menu acessar login config mobile app submenu',
  },
  {
    title: '3. Gerenciamento de Categorias',
    content: (
      <>
        <Heading size="sm" mb={2}>3.1 Visualizando Categorias</Heading>
        <Text mb={2}>Ao acessar a página de Categorias, você verá:</Text>
        <UnorderedList ml={6} mb={4}>
          <ListItem>Um <strong>contador</strong> com o total de categorias cadastradas</ListItem>
          <ListItem>Uma <strong>barra de busca</strong> para filtrar categorias pelo nome</ListItem>
          <ListItem>Botões para alternar entre <strong>visualização em lista</strong> e <strong>visualização em grade</strong></ListItem>
          <ListItem>Botão <strong>"Atualizar"</strong> para recarregar os dados</ListItem>
          <ListItem>Botão <strong>"Nova Categoria"</strong> para adicionar uma nova categoria</ListItem>
        </UnorderedList>

        <Heading size="sm" mb={2}>3.2 Criando uma Nova Categoria</Heading>
        <OrderedList spacing={1} mb={2}>
          <ListItem>Clique no botão <strong>"Nova Categoria"</strong></ListItem>
          <ListItem>Uma janela (modal) será aberta com o formulário</ListItem>
          <ListItem>Preencha os campos obrigatórios:</ListItem>
        </OrderedList>
        <Table size="sm" mb={3} variant="simple">
          <Thead><Tr><Th>Campo</Th><Th>Descrição</Th><Th>Obrigatório</Th></Tr></Thead>
          <Tbody>
            <Tr><Td fontWeight="medium">Nome da Categoria</Td><Td>Nome que aparecerá no aplicativo (ex: "Construção Civil")</Td><Td>Sim</Td></Tr>
            <Tr><Td fontWeight="medium">Ícone da Categoria (SVG ou XML)</Td><Td>Arquivo de ícone no formato .svg ou .xml</Td><Td>Sim</Td></Tr>
          </Tbody>
        </Table>
        <OrderedList start={4} spacing={1} mb={3}>
          <ListItem>Para adicionar o ícone: clique em <strong>"Selecionar Arquivo"</strong>, escolha um arquivo .svg ou .xml. O nome aparecerá em uma etiqueta verde com pré-visualização.</ListItem>
          <ListItem>Clique em <strong>"Adicionar"</strong> para salvar a categoria</ListItem>
        </OrderedList>

        <Heading size="sm" mb={2}>3.3 Editando uma Categoria</Heading>
        <OrderedList spacing={1} mb={4}>
          <ListItem>Localize a categoria e clique no <strong>ícone de lápis</strong> (editar)</ListItem>
          <ListItem>A janela de edição abrirá com os dados atuais preenchidos</ListItem>
          <ListItem>Modifique o <strong>nome</strong> e/ou o <strong>ícone</strong></ListItem>
          <ListItem>Clique em <strong>"Atualizar"</strong> e depois <strong>"Confirmar"</strong></ListItem>
        </OrderedList>

        <Heading size="sm" mb={2}>3.4 Excluindo uma Categoria</Heading>
        <OrderedList spacing={1} mb={2}>
          <ListItem>Localize a categoria e clique no <strong>ícone de lixeira</strong> (excluir)</ListItem>
          <ListItem>Leia o aviso de confirmação</ListItem>
          <ListItem>Clique em <strong>"Excluir"</strong> para confirmar</ListItem>
        </OrderedList>
        <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          Excluir uma categoria pode afetar todas as subcategorias vinculadas. Certifique-se de que não há subcategorias ativas associadas.
        </Alert>
      </>
    ),
    keywords: 'categorias criar editar excluir buscar ícone svg xml visualizar lista grade',
  },
  {
    title: '4. Gerenciamento de Subcategorias',
    content: (
      <>
        <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          Para criar subcategorias, é obrigatório ter pelo menos uma categoria cadastrada.
        </Alert>

        <Heading size="sm" mb={2}>Criando uma Nova Subcategoria</Heading>
        <OrderedList spacing={1} mb={4}>
          <ListItem>Clique no botão <strong>"Nova Subcategoria"</strong></ListItem>
          <ListItem>Preencha o <strong>nome</strong> e selecione a <strong>categoria principal</strong> no dropdown</ListItem>
          <ListItem>Clique em <strong>"Adicionar"</strong> para salvar</ListItem>
        </OrderedList>

        <Heading size="sm" mb={2}>Editando / Excluindo</Heading>
        <Text mb={2}>Use os ícones de <strong>lápis</strong> (editar) e <strong>lixeira</strong> (excluir) na coluna de ações. O processo é o mesmo das categorias.</Text>

        <Text mt={3}>A busca filtra por <strong>nome da subcategoria</strong> e <strong>nome da categoria</strong>.</Text>
      </>
    ),
    keywords: 'subcategorias criar editar excluir buscar profissão categoria principal dropdown',
  },
  {
    title: '5. Cores do Aplicativo',
    content: (
      <>
        <Text mb={3}>
          Acesse via: <strong>Config. Mobile App → Cores do Aplicativo</strong>. Esta página permite configurar o esquema de cores do aplicativo mobile.
        </Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem>Clique em qualquer cor para abrir o seletor de cores (ChromePicker)</ListItem>
          <ListItem>Modifique as cores desejadas: cor de fundo, cor primária, cor secundária, cor do texto, cor da barra de busca</ListItem>
          <ListItem>Clique em <strong>"Salvar"</strong> para aplicar as alterações</ListItem>
          <ListItem>Use <strong>"Redefinir Padrão"</strong> para voltar às cores originais</ListItem>
        </UnorderedList>
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          As cores são salvas no formato hexadecimal com prefixo 0xFF (ex: <Code>0xFF4BEDFF</Code>) para compatibilidade com o aplicativo Android.
        </Alert>
      </>
    ),
    keywords: 'cores aplicativo tema mobile configurar hexadecimal android',
  },
  {
    title: '6. Observações Importantes',
    content: (
      <Table size="sm" variant="simple">
        <Thead><Tr><Th>Regra</Th><Th>Descrição</Th></Tr></Thead>
        <Tbody>
          <Tr><Td fontWeight="medium">Ordem de criação</Td><Td>Sempre crie as categorias primeiro, depois as subcategorias</Td></Tr>
          <Tr><Td fontWeight="medium">Ícones</Td><Td>Apenas arquivos nos formatos <Code>.svg</Code> e <Code>.xml</Code> são aceitos</Td></Tr>
          <Tr><Td fontWeight="medium">Exclusão irreversível</Td><Td>Todas as exclusões são permanentes e não podem ser desfeitas</Td></Tr>
          <Tr><Td fontWeight="medium">Busca</Td><Td>A busca é feita em tempo real e não diferencia maiúsculas de minúsculas</Td></Tr>
        </Tbody>
      </Table>
    ),
    keywords: 'regras observações ícones exclusão irreversível obrigatório busca',
  },
];

export default configManual;
